import { ArrowSquareOut, FilmSlate } from '@phosphor-icons/react';
import type {
  PersonaPostDto,
  PersonaPostPlatformDto,
  Platform as ApiPlatform,
} from '@reelplicate/api-client/model';
import { useListPersonaPosts } from '@reelplicate/api-client/personas';
import {
  cn,
  EmptyState,
  FilterChip,
  Inline,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@reelplicate/ui';
import { useMemo, useState } from 'react';

import { formatCount, formatDay, formatPercent } from '@/features/analytics/format';
import { API_PLATFORM_NAMES, toPlatform } from '@/features/calendar/calendar-model';
import { PlatformDot } from '@/features/calendar/platform-stack';

type Counts = Pick<
  PersonaPostPlatformDto,
  'views' | 'likes' | 'comments' | 'shares' | 'saves' | 'engagement'
>;
type SortKey = 'postedAt' | keyof Counts;

const COLUMNS: { key: keyof Counts; label: string }[] = [
  { key: 'views', label: 'Views' },
  { key: 'likes', label: 'Likes' },
  { key: 'comments', label: 'Comments' },
  { key: 'shares', label: 'Shares' },
  { key: 'saves', label: 'Saves' },
  { key: 'engagement', label: 'Engagement' },
];
const KIND_LABEL = { VIDEO: 'Reel', CAROUSEL: 'Carousel', IMAGE: 'Photo' } as const;
const rowLine = 'shadow-[inset_0_1px_0_0_var(--color-border-subtle)]';

/** A count summed over platforms; null when none of them reports it. */
const sum = (rows: PersonaPostPlatformDto[], key: Exclude<keyof Counts, 'engagement'>) =>
  rows.some((r) => r[key] !== null)
    ? rows.reduce((total, r) => total + (r[key] ?? 0), 0)
    : null;

/** A post's numbers on one platform, or summed over all of them. */
function postCounts(post: PersonaPostDto, platform: ApiPlatform | 'ALL'): Counts | null {
  const rows =
    platform === 'ALL'
      ? post.platforms
      : post.platforms.filter((p) => p.platform === platform);
  if (rows.length === 0) return null;
  if (rows.length === 1) return rows[0]!;
  const views = sum(rows, 'views');
  const interactions = (['likes', 'comments', 'shares', 'saves'] as const).reduce(
    (total, key) => total + (sum(rows, key) ?? 0),
    0,
  );
  return {
    views,
    likes: sum(rows, 'likes'),
    comments: sum(rows, 'comments'),
    shares: sum(rows, 'shares'),
    saves: sum(rows, 'saves'),
    engagement: views ? Math.round((interactions / views) * 1000) / 10 : null,
  };
}

const show = (value: number | null, percent = false) =>
  value === null ? '—' : percent ? formatPercent(value) : formatCount(value);

/**
 * Every post found on the persona's accounts, with how it did on each
 * platform. Sorting a column brings their winners to the top; a platform
 * chip shows that platform's numbers alone.
 */
export function PersonaPosts({
  workspaceId,
  personaId,
  name,
}: {
  workspaceId: string;
  personaId: string;
  name: string;
}) {
  const { data, isPending } = useListPersonaPosts(workspaceId, personaId, {
    query: { enabled: Boolean(workspaceId && personaId) },
  });
  const [platform, setPlatform] = useState<ApiPlatform | 'ALL'>('ALL');
  const [sort, setSort] = useState<{ key: SortKey; dir: 'descending' | 'ascending' }>({
    key: 'postedAt',
    dir: 'descending',
  });

  const posts = useMemo(() => data?.items ?? [], [data]);
  const platforms = useMemo(
    () => [...new Set(posts.flatMap((p) => p.platforms.map((r) => r.platform)))].sort(),
    [posts],
  );
  const rows = useMemo(() => {
    const value = (post: PersonaPostDto, counts: Counts) =>
      sort.key === 'postedAt'
        ? post.postedAt
          ? new Date(post.postedAt).getTime()
          : null
        : counts[sort.key];
    return posts
      .flatMap((post) => {
        const counts = postCounts(post, platform);
        return counts ? [{ post, counts }] : [];
      })
      .sort((a, b) => {
        const [x, y] = [value(a.post, a.counts), value(b.post, b.counts)];
        if (x === null || y === null) return x === null ? (y === null ? 0 : 1) : -1;
        return sort.dir === 'descending' ? y - x : x - y;
      });
  }, [posts, platform, sort]);

  const toggleSort = (key: SortKey) =>
    setSort((prev) =>
      prev.key === key
        ? { key, dir: prev.dir === 'descending' ? 'ascending' : 'descending' }
        : { key, dir: 'descending' },
    );

  if (isPending) {
    return (
      <Stack gap={2} className="py-5">
        {Array.from({ length: 3 }, (_, i) => (
          <Skeleton key={i} shape="rectangle" shimmer className="h-14" />
        ))}
      </Stack>
    );
  }
  if (posts.length === 0) {
    return (
      <EmptyState
        className="py-10"
        icon={FilmSlate}
        title="No posts found yet"
        description={`Posts show up here once the sync finds them on ${name}'s accounts.`}
      />
    );
  }

  return (
    <Stack gap={3} className="py-5">
      {platforms.length > 1 && (
        <Inline gap={2} wrap>
          <FilterChip
            selected={platform === 'ALL'}
            onSelectedChange={() => setPlatform('ALL')}
            count={posts.length}
          >
            All platforms
          </FilterChip>
          {platforms.map((p) => (
            <FilterChip
              key={p}
              selected={platform === p}
              onSelectedChange={() => setPlatform(p)}
              count={
                posts.filter((post) => post.platforms.some((r) => r.platform === p))
                  .length
              }
            >
              {API_PLATFORM_NAMES[p]}
            </FilterChip>
          ))}
        </Inline>
      )}
      <div className="-mx-6 overflow-x-auto">
        <Table fixed aria-label={`${name}'s posts`} className="min-w-[860px]">
          <TableHeader>
            <TableRow>
              <TableHead
                sort={sort.key === 'postedAt' ? sort.dir : 'none'}
                onSort={() => toggleSort('postedAt')}
                className={cn('h-auto py-2.5 pr-2 pl-6', rowLine)}
              >
                Post
              </TableHead>
              {COLUMNS.map(({ key, label }) => (
                <TableHead
                  key={key}
                  align="right"
                  width={key === 'engagement' ? 104 : 84}
                  sort={sort.key === key ? sort.dir : 'none'}
                  onSort={() => toggleSort(key)}
                  className={cn(
                    'h-auto py-2.5 pl-0',
                    key === 'engagement' ? 'pr-6' : 'pr-2',
                    rowLine,
                  )}
                >
                  {label}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.flatMap(({ post, counts }) => {
              const breakdown =
                platform === 'ALL' && post.platforms.length > 1 ? post.platforms : [];
              return [
                <TableRow key={post.id}>
                  <TableCell className={cn('h-auto py-3 pr-2 pl-6', rowLine)}>
                    <PostCell post={post} platform={platform} />
                  </TableCell>
                  <NumberCells counts={counts} strong />
                </TableRow>,
                ...breakdown.map((row) => (
                  <TableRow
                    key={`${post.id}:${row.accountId}`}
                    className="bg-bg-subtle/50"
                  >
                    <TableCell className={cn('h-auto py-2 pr-2 pl-6', rowLine)}>
                      <PlatformLine row={row} />
                    </TableCell>
                    <NumberCells counts={row} />
                  </TableRow>
                )),
              ];
            })}
          </TableBody>
        </Table>
      </div>
    </Stack>
  );
}

function PostCell({
  post,
  platform,
}: {
  post: PersonaPostDto;
  platform: ApiPlatform | 'ALL';
}) {
  const shown =
    platform === 'ALL'
      ? post.platforms
      : post.platforms.filter((p) => p.platform === platform);
  const single = shown.length === 1 ? shown[0] : undefined;
  return (
    <div className="flex min-w-0 items-center gap-3">
      {post.thumbnailUrl ? (
        <img
          src={post.thumbnailUrl}
          alt=""
          className="h-[52px] w-[30px] shrink-0 rounded-sm object-cover"
        />
      ) : (
        <span aria-hidden className="bg-bg-muted h-[52px] w-[30px] shrink-0 rounded-sm" />
      )}
      <Stack gap={0.5} className="min-w-0">
        <span className="text-body-sm-strong text-text-primary truncate">
          {single?.url ? (
            <a
              href={single.url}
              target="_blank"
              rel="noreferrer"
              className="hover:underline"
            >
              {post.title}
            </a>
          ) : (
            post.title
          )}
        </span>
        <span className="text-caption text-text-tertiary flex items-center gap-1.5">
          {shown.map((r) => {
            const mark = toPlatform(r.platform);
            return mark ? (
              <PlatformDot key={r.accountId} platform={mark} size="sm" />
            ) : null;
          })}
          {[
            post.postedAt ? formatDay(new Date(post.postedAt)) : null,
            post.kind ? KIND_LABEL[post.kind] : null,
            post.calendarPostId ? 'From the Calendar' : null,
          ]
            .filter(Boolean)
            .join(' · ')}
        </span>
      </Stack>
    </div>
  );
}

function PlatformLine({ row }: { row: PersonaPostPlatformDto }) {
  const mark = toPlatform(row.platform);
  return (
    <span className="text-body-sm text-text-secondary flex items-center gap-2 pl-[42px]">
      {mark && <PlatformDot platform={mark} size="sm" />}
      {API_PLATFORM_NAMES[row.platform]} {row.handle}
      {row.url && (
        <a
          href={row.url}
          target="_blank"
          rel="noreferrer"
          aria-label={`Open on ${API_PLATFORM_NAMES[row.platform]}`}
          className="text-icon-secondary hover:text-text-primary"
        >
          <ArrowSquareOut aria-hidden className="size-3.5" />
        </a>
      )}
    </span>
  );
}

function NumberCells({ counts, strong }: { counts: Counts; strong?: boolean }) {
  return COLUMNS.map(({ key }) => (
    <TableCell
      key={key}
      className={cn('h-auto py-2 pl-0', key === 'engagement' ? 'pr-6' : 'pr-2', rowLine)}
    >
      <span
        className={cn(
          'text-text-primary w-full truncate text-right tabular-nums',
          strong && key === 'views' ? 'text-body-sm-strong' : 'text-body-sm',
        )}
      >
        {show(counts[key], key === 'engagement')}
      </span>
    </TableCell>
  ));
}
